import {Injectable, OnDestroy, inject} from '@angular/core';
import * as configcat from 'configcat-js';
import {IConfigCatClient} from 'configcat-js';
import {BehaviorSubject, from, Observable, of} from 'rxjs';
import {catchError, distinctUntilChanged, filter, shareReplay, switchMap} from 'rxjs/operators';
import {CurrentTenantStore} from '../tenant/current-tenant.store';
import {AuthenticationService} from '../auth/authentication.service';
import {environment} from '../../../environments/environment';
import {FeatureFlag} from '../../models/feature-flags.model';

@Injectable({providedIn: 'root'})
export class FeatureFlagService implements OnDestroy {
	private tenantStore = inject(CurrentTenantStore);
	private authenticationService = inject(AuthenticationService);

	private client: IConfigCatClient;
	private userSubject = new BehaviorSubject<configcat.User | undefined>(undefined);
	private flagCache = new Map<string, Observable<boolean>>();

	constructor() {
		this.client = configcat.getClient(environment.configCatSdkKey, configcat.PollingMode.AutoPoll, {
			pollIntervalSeconds: 60,
			logger: configcat.createConsoleLogger(configcat.LogLevel.Warn),
			setupHooks: (hooks) => {
				hooks.on('configChanged', () => {
					const currentUser = this.userSubject.getValue();
					if (currentUser) {
						this.userSubject.next(currentUser);
					}
				});
			}
		});

		this.tenantStore.whenLoaded().subscribe(tenant => {
			const userInfo = this.authenticationService.getUserInfo();
			const lang = localStorage.getItem('lang') || 'en';

			const user = new configcat.User(
				tenant.configuration.id,
				userInfo?.email ?? undefined,
				undefined,
				{
					tenantId: tenant.configuration.id,
					tenantName: tenant.configuration.name,
					role: tenant.configuration.roleName ?? '',
					language: lang,
					fullName: userInfo ? `${userInfo.firstName} ${userInfo.lastName}` : '',
					email: userInfo?.email ?? '',
				}
			);
			this.userSubject.next(user);
		});
	}

	isFeatureEnabled(flag: FeatureFlag | string): Observable<boolean> {
		if (!this.flagCache.has(flag)) {
			const flag$ = this.userSubject.pipe(
				filter(user => user !== undefined),
				switchMap(user => from(this.client.getValueAsync(flag, false, user!))),
				distinctUntilChanged(),
				shareReplay(1),
				catchError(() => of(false))
			);
			this.flagCache.set(flag, flag$);
		}
		return this.flagCache.get(flag)!;
	}

	forceRefresh(): void {
		this.client.forceRefreshAsync();
		const currentUser = this.userSubject.getValue();
		if (currentUser) {
			this.userSubject.next(currentUser);
		}
	}

	ngOnDestroy(): void {
		this.client.dispose();
	}
}
